import { SxProps } from "@mui/material";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { MultipleUserPickerQuery } from "generated-graphql/graphql";
import React, { forwardRef, useCallback, useMemo } from "react";
import { ControllerFieldState } from "react-hook-form";

import { PickerInput, useMultiPickerWithHydration } from "../hooks/useMultiPickerWithHydration";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

const USERS_QUERY = gql(`
  query MultipleUserPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        person {
          first
          last
        }
      }
      count
    }
  }
`);

export type UserOption = {
  id: string;
  email: string;
  name: string;
};

interface MultipleUserPickerProps extends Partial<ControllerFieldState> {
  onChange: (users: UserOption[] | null) => void;
  defaultSearchTerm?: string;
  disabledUsersById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?: (UserOption | PickerInput<UserOption> | string)[] | null;
  lazyLoad?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  showErrors?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export const MultipleUserPicker: React.FC<MultipleUserPickerProps> = forwardRef<
  HTMLDivElement,
  MultipleUserPickerProps
>(function MultipleUserPicker(
  {
    onChange,
    defaultSearchTerm = "",
    disabledUsersById = [],
    disabledOptionLabel = "",
    label = "User",
    value,
    error,
    disabled,
    showErrors = false,
    sx,
    onOpen,
    onClose,
  },
  ref
) {
  const getItems = useCallback((data: MultipleUserPickerQuery) => {
    return (
      data.users?.items.map((user) => ({
        id: user.id,
        email: user.email,
        name: user.person ? getFullName(user.person) : user.email,
      })) ?? []
    );
  }, []);

  const normalizedValue = useMemo(() => {
    return value?.map((v) => {
      if (typeof v === "string") {
        return { id: v, email: v };
      }
      return v;
    });
  }, [value]);

  const { resolvedValue, isHydrating, enhancedOnChange, enhancedGetItems } =
    useMultiPickerWithHydration<UserOption, MultipleUserPickerQuery>({
      value: normalizedValue || null,
      query: USERS_QUERY,
      getItems,
      cacheKey: "email",
      idsSearchPrefix: "email",
      onChange,
    });

  const getOptionLabel = useCallback((user: UserOption) => {
    if (!user) return "";
    return user.name !== user.email ? `${user.name} (${user.email})` : user.email;
  }, []);

  const isOptionEqualToValue = useCallback(
    (option: UserOption, value: UserOption) => option?.email === value?.email,
    []
  );

  return (
    <>
      <AsyncAutocomplete
        ref={ref}
        query={USERS_QUERY}
        initialPageSize={20}
        variables={{}}
        getItems={enhancedGetItems}
        getOptionLabel={getOptionLabel}
        placeholderLabel={label}
        onChange={enhancedOnChange}
        value={resolvedValue}
        error={error}
        disabled={disabled || isHydrating}
        additionalSearchString={defaultSearchTerm}
        multiple={true}
        filterSelectedOptions
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionDisabled={(option: UserOption) => disabledUsersById.includes(option?.id ?? "")}
        loading={isHydrating}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {getOptionLabel(option)}
            {disabledUsersById.includes(option?.id ?? "") && (
              <span>&nbsp;({disabledOptionLabel})</span>
            )}
          </li>
        )}
        sx={sx}
        onOpen={onOpen}
        onClose={onClose}
      />
      {showErrors && <ErrorDisplay error={error} />}
    </>
  );
});
